import { gql } from '@apollo/client';
import { useQuery } from '@tbitop/api';
import { useGraphQLErrorHandler } from '@tbitop/error-handling';
import { useDebouncedState } from '@tbitop/ui';
import { defaultTo, isNull } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';

import { toSortierkriterium, toSortierrichtung } from '../../core/application/sorting';
import type { ObjektFilters, ObjektSorting } from '../../ui/controller/objekt-filter';
import { ObjektSortierung, Sortierrichtung } from './__generated__/globalTypes';
import type { Objekte_PagedQuery, Objekte_PagedQueryVariables } from './__generated__/objekte';

type Unarray<T> = T extends (infer U)[] ? U : T;
type Objekte_Paged_objekteSeitenweise_aktuelleSeite = Unarray<Objekte_PagedQuery['objekteSeitenweise']['aktuelleSeite']>;

const isObjekt = (objekt: Objekte_Paged_objekteSeitenweise_aktuelleSeite | null): objekt is Objekte_Paged_objekteSeitenweise_aktuelleSeite => {
  return !isNull(objekt);
};

export const useObjekte = (sorting: ObjektSorting, filters: ObjektFilters) => {
  const debouncedFilters = useDebouncedState(filters, 500);
  const { pagination, pageLoading, objekte, loading, refresh } = useObjekteQuery(
    toSortierkriterium(sorting, ObjektSortierung),
    toSortierrichtung(sorting, Sortierrichtung),
    debouncedFilters,
  );

  return { pagination, pageLoading, objekte, loading, refresh };
};

const initialPage = 1;
const entriesPerPage = 25;

export const useObjekteQuery = (sortierung: ObjektSortierung = ObjektSortierung.Standard, sortierrichtung: Sortierrichtung = Sortierrichtung.Aufsteigend, filter: ObjektFilters) => {
  const { handleWithErrorNotification } = useGraphQLErrorHandler();
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageLoading, setPageLoading] = useState(false);
  const variables = useMemo(() => {
    return {
      filter,
      sortierung,
      sortierrichtung,
    };
  }, [filter, sortierung, sortierrichtung]);

  const { data, fetchMore, loading, refetch, client } = useQuery<Objekte_PagedQuery, Objekte_PagedQueryVariables>(gql`
    query Objekte_Paged($anzahlProSeite: Int!, $seite: Int!, $filter: ObjektFilterInput, $sortierung: ObjektSortierung!, $sortierrichtung: Sortierrichtung!) {
      objekteSeitenweise(seite: $seite, anzahlProSeite: $anzahlProSeite, filter: $filter, sortierung: $sortierung, sortierrichtung: $sortierrichtung) {
        beschreibung {
          gesamtAnzahl
          hatNachfolger
        }
        aktuelleSeite {
          id
          name
          name2
          adresse {
            strasse
            postleitzahl
            ort
          }
          aufgaben {
            status
          }
          nummer
          aufgabenStatistik {
            status
            anzahl
          }
        }
      }
    }
  `, {
    variables: {
      ...variables,
      seite: initialPage,
      anzahlProSeite: entriesPerPage,
    },
    onError: handleWithErrorNotification('Es konnten keine Objekte geladen werden.'),
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    // clear all data
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'offeneAufgabenStatistik' });
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'objekteSeitenweise', broadcast: false });
    setCurrentPage(initialPage);
  }, [client.cache, variables]);

  const total = data?.objekteSeitenweise.beschreibung.gesamtAnzahl;
  const objekte = defaultTo(data?.objekteSeitenweise.aktuelleSeite.filter(isObjekt), []);

  return {
    objekte,
    total,
    loading,
    pageLoading,
    pagination: {
      fetchMore: async () => {
        setPageLoading(true);
        const nextPage = currentPage + 1;
        return fetchMore({
          variables: {
            seite: nextPage,
          },
        })
          .then(() => {
            setCurrentPage(nextPage);
          })
          .finally(() => {
            setPageLoading(false);
          })
          .catch(handleWithErrorNotification('Das Laden weiterer Objekte ist fehlgeschlagen.'));
      },
      hasMore: defaultTo(data?.objekteSeitenweise.beschreibung.hatNachfolger, false),
    },
    refresh: async () => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'offeneAufgabenStatistik' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'objekteSeitenweise', broadcast: false });
      return refetch(variables)
        .catch(handleWithErrorNotification('Das Laden weiterer Objekte ist fehlgeschlagen.'));
    },
  };
};
